// CanvasState.js - Handles all canvas data, persistence, and history

export const FONT_SIZE_SCALE_FACTOR = 1.25;
const FONT_SIZE_VERSION = 2;
const BASE_GLOBAL_FONT_SIZE = 21;
export const DEFAULT_GLOBAL_FONT_SIZE =
    Math.round(BASE_GLOBAL_FONT_SIZE * FONT_SIZE_SCALE_FACTOR * 100) / 100;

function scaleNumericValue(value, factor = FONT_SIZE_SCALE_FACTOR) {
    if (typeof value !== 'number' || Number.isNaN(value)) {
        return value;
    }
    return Math.round(value * factor * 100) / 100;
}

function upgradeNodeFontMetrics(node, factor = FONT_SIZE_SCALE_FACTOR) {
    if (!node || typeof node !== 'object') {
        return node;
    }

    const keysToScale = ['fontSize', 'height', 'maxHeight', 'contentHeight', 'scrollY'];
    keysToScale.forEach(key => {
        if (typeof node[key] === 'number') {
            node[key] = scaleNumericValue(node[key], factor);
        }
    });

    return node;
}
export class CanvasState {
    constructor() {
        // Core canvas data
        this.nodes = [];
        this.connections = [];
        this.selectedNodes = [];
        this.selectedConnection = null;
        
        // View state
        this.offsetX = 0;
        this.offsetY = 0;
        this.scale = 1.0;
        this.globalFontSize = DEFAULT_GLOBAL_FONT_SIZE;
        this.fontSizeVersion = FONT_SIZE_VERSION;
        
        // History for undo/redo
        this.history = [];
        this.historyIndex = -1;
        this.maxHistorySize = 100;
        
        // Copy/paste clipboard
        this.clipboard = {
            nodes: [],
            connections: []
        };
        
        // Performance optimization
        this.spatialGrid = new Map();
        this.gridSize = 200;
        this.visibleNodes = new Set();

        // Thread-aware canvas management
        this.activeThreadId = null;
    }

    // Node management - using Obsidian Canvas format
    createNode(text = 'New Node', x = null, y = null, generatedByModel = null, { save = true, threadId = undefined } = {}) {
        const node = {
            id: (Date.now() + Math.random()).toString(),
            type: "text",
            text: text,
            x: x !== null ? x : 400,
            y: y !== null ? y : 300,
            width: 360,
            height: scaleNumericValue(140),
            // Internal properties (not part of Obsidian format but needed for our functionality)
            maxHeight: scaleNumericValue(400),
            scrollY: 0,
            isSelected: false,
            generatedByModel: generatedByModel,
            fontSize: this.globalFontSize,
            // Per-node generation states to allow simultaneous AI operations
            isGeneratingAI: false,
            isGeneratingImage: false,
            isGeneratingVideo: false,
            isGeneratingContext: false,
            isGeneratingReference: false,
            isUploadingImage: false
        };

        const resolvedThreadId = this.resolveThreadId(threadId);
        node.threadId = resolvedThreadId;

        const adjustedPosition = this.findNonOverlappingPosition(node.x, node.y, node.width, node.height);
        node.x = adjustedPosition.x;
        node.y = adjustedPosition.y;

        this.nodes.push(node);
        this.addNodeToGrid(node);
        if (save) {
            this.saveState();
        }
        return node;
    }

    findNonOverlappingPosition(targetX, targetY, width = 360, height = 140, margin = 80) {
        const pad = margin / 2;
        const xStep = width + margin;
        const yStep = height + margin;
        const maxRadius = 8;
        const visited = new Set();

        const isFiniteNumber = value => typeof value === 'number' && Number.isFinite(value);

        const doesOverlap = (candidateX, candidateY) => {
            const left = candidateX - pad;
            const right = candidateX + width + pad;
            const top = candidateY - pad;
            const bottom = candidateY + height + pad;

            return this.nodes.some(existing => {
                if (!existing) return false;
                const existingX = isFiniteNumber(existing.x) ? existing.x : 0;
                const existingY = isFiniteNumber(existing.y) ? existing.y : 0;
                const existingWidth = isFiniteNumber(existing.width) ? existing.width : 360;
                const existingHeight = isFiniteNumber(existing.height) ? existing.height : 140;

                const existingLeft = existingX - pad;
                const existingRight = existingX + existingWidth + pad;
                const existingTop = existingY - pad;
                const existingBottom = existingY + existingHeight + pad;

                const horizontalOverlap = left < existingRight && right > existingLeft;
                const verticalOverlap = top < existingBottom && bottom > existingTop;
                return horizontalOverlap && verticalOverlap;
            });
        };

        for (let radius = 0; radius <= maxRadius; radius++) {
            for (let dx = -radius; dx <= radius; dx++) {
                for (let dy = -radius; dy <= radius; dy++) {
                    if (radius !== 0 && Math.abs(dx) !== radius && Math.abs(dy) !== radius) {
                        continue;
                    }

                    const candidateX = targetX + dx * xStep;
                    const candidateY = targetY + dy * yStep;
                    const key = `${candidateX},${candidateY}`;
                    if (visited.has(key)) {
                        continue;
                    }
                    visited.add(key);

                    if (!doesOverlap(candidateX, candidateY)) {
                        return { x: candidateX, y: candidateY };
                    }
                }
            }
        }

        return { x: targetX, y: targetY };
    }
    
    deleteSelectedNodes() {
        if (this.selectedNodes.length === 0) return;

        const nodeIdsToDelete = new Set(this.selectedNodes.map(node => node.id));

        // Clean up video and iframe elements
        this.selectedNodes.forEach(node => {
            if (node._videoObj) {
                try {
                    if (node._videoObj.parentNode) {
                        node._videoObj.parentNode.removeChild(node._videoObj);
                    }
                } catch (e) {
                    console.warn('Failed to remove video element:', e);
                }
                node._videoObj = null;
            }
            if (node._iframeObj) {
                try {
                    if (node._iframeObj.parentNode) {
                        node._iframeObj.parentNode.removeChild(node._iframeObj);
                    }
                } catch (e) {
                    console.warn('Failed to remove iframe element:', e);
                }
                node._iframeObj = null;
            }
        });

        // Remove nodes from spatial grid
        this.selectedNodes.forEach(node => this.removeNodeFromGrid(node));

        // Remove the nodes
        this.nodes = this.nodes.filter(node => !nodeIdsToDelete.has(node.id));

        // Remove connections involving these nodes
        this.connections = this.connections.filter(
            conn => !nodeIdsToDelete.has(conn.fromNode) && !nodeIdsToDelete.has(conn.toNode)
        );

        this.saveState();
        this.clearSelection();
    }
    
    // Connection management - using Obsidian Canvas format
    createConnection(fromNode, toNode, fromSide = null, toSide = null) {
        const existingConnection = this.connections.find(
            conn => (conn.fromNode === fromNode.id.toString() && conn.toNode === toNode.id.toString()) ||
                   (conn.fromNode === toNode.id.toString() && conn.toNode === fromNode.id.toString())
        );

        if (!existingConnection) {
            const connection = {
                id: (Date.now() + Math.random()).toString(),
                fromNode: fromNode.id.toString(),
                toNode: toNode.id.toString()
            };
            
            // Add connection points if specified
            if (fromSide) connection.fromSide = fromSide;
            if (toSide) connection.toSide = toSide;
            
            this.connections.push(connection);
            this.saveState();
            return connection;
        }
        return null;
    }
    
    getConnectedNodes(node) {
        if (!node) return [];
        const connectedNodes = [];
        const nodeId = node.id.toString();
        this.connections.forEach(connection => {
            let connectedNodeId = null;
            if (connection.fromNode === nodeId) {
                connectedNodeId = connection.toNode;
            } else if (connection.toNode === nodeId) {
                connectedNodeId = connection.fromNode;
            }
            if (connectedNodeId) {
                const connectedNode = this.nodes.find(n => n.id.toString() === connectedNodeId);
                if (connectedNode) connectedNodes.push(connectedNode);
            }
        });
        return connectedNodes;
    }
    
    getAncestorNodes(node) {
        if (!node) return [];
        const ancestors = [];
        const visited = new Set();
        const nodesToProcess = [node];
        const nodeId = node.id.toString();

        while (nodesToProcess.length > 0) {
            const currentNode = nodesToProcess.shift();
            const currentNodeId = currentNode.id.toString();
            if (visited.has(currentNodeId)) continue;
            visited.add(currentNodeId);

            const parentConnections = this.connections.filter(connection =>
                connection.toNode === currentNodeId
            );

            if (parentConnections.length > 0) {
                parentConnections.forEach(parentConnection => {
                    const parentNode = this.nodes.find(n => n.id.toString() === parentConnection.fromNode);
                    if (parentNode && !visited.has(parentNode.id.toString())) {
                        ancestors.push(parentNode);
                        nodesToProcess.push(parentNode);
                    }
                });
            }
        }
        return ancestors;
    }
    
    // Selection management
    selectNodes(nodes) {
        this.clearSelection();
        if (Array.isArray(nodes)) {
            nodes.forEach(node => {
                if (node) {
                    node.isSelected = true;
                    this.selectedNodes.push(node);
                }
            });
        } else if (nodes) {
            nodes.isSelected = true;
            this.selectedNodes.push(nodes);
        }
    }
    
    addToSelection(node) {
        if (node && !this.selectedNodes.includes(node)) {
            node.isSelected = true;
            this.selectedNodes.push(node);
        }
    }
    
    removeFromSelection(node) {
        if (node) {
            node.isSelected = false;
            const index = this.selectedNodes.indexOf(node);
            if (index > -1) {
                this.selectedNodes.splice(index, 1);
            }
        }
    }
    
    clearSelection() {
        this.nodes.forEach(n => n.isSelected = false);
        this.selectedNodes = [];
        this.selectedConnection = null;
    }
    
    get selectedNode() {
        return this.selectedNodes.length > 0 ? this.selectedNodes[0] : null;
    }
    
    // Copy/Paste
    copySelectedNodes() {
        if (this.selectedNodes.length === 0) return false;
        
        const selectedNodeIds = new Set(this.selectedNodes.map(node => node.id));
        
        this.clipboard.nodes = this.selectedNodes.map(node => ({
            ...node,
            isSelected: false,
            _imgObj: null,
            _videoObj: null
        }));
        
        this.clipboard.connections = this.connections.filter(conn => 
            selectedNodeIds.has(conn.fromNode) && selectedNodeIds.has(conn.toNode)
        ).map(conn => ({ ...conn }));
        
        return true;
    }
    
    pasteNodes() {
        if (this.clipboard.nodes.length === 0) return [];

        const oldToNewIdMap = new Map();
        const pastedNodes = [];
        const pasteOffset = { x: 50, y: 50 };

        // Create new nodes with new IDs
        this.clipboard.nodes.forEach(originalNode => {
            const newId = Date.now() + Math.random();
            oldToNewIdMap.set(originalNode.id, newId);

            const newNode = {
                ...originalNode,
                id: newId,
                x: originalNode.x + pasteOffset.x,
                y: originalNode.y + pasteOffset.y,
                isSelected: true
            };

            const targetThread = this.resolveThreadId(this.activeThreadId || originalNode.threadId || undefined);
            newNode.threadId = targetThread;

            this.nodes.push(newNode);
            this.addNodeToGrid(newNode);
            pastedNodes.push(newNode);
        });
        
        // Create new connections
        this.clipboard.connections.forEach(originalConnection => {
            const newFromId = oldToNewIdMap.get(originalConnection.fromNode);
            const newToId = oldToNewIdMap.get(originalConnection.toNode);
            
            if (newFromId && newToId) {
                const newConnection = {
                    ...originalConnection,
                    id: (Date.now() + Math.random()).toString(),
                    fromNode: newFromId,
                    toNode: newToId
                };
                this.connections.push(newConnection);
            }
        });
        
        // Update selection
        this.saveState();
        this.nodes.forEach(node => {
            if (!pastedNodes.includes(node)) {
                node.isSelected = false;
            }
        });
        this.selectedNodes = pastedNodes;
        this.selectedConnection = null;
        
        return pastedNodes;
    }
    
    // History management
    saveState() {
        // Clear redo stack if we are in the middle of history
        if (this.historyIndex < this.history.length - 1) {
            this.history.splice(this.historyIndex + 1);
        }

        const state = {
            nodes: JSON.parse(JSON.stringify(this.nodes)),
            connections: JSON.parse(JSON.stringify(this.connections)),
            globalFontSize: this.globalFontSize,
            activeThreadId: this.activeThreadId,
            fontSizeVersion: this.fontSizeVersion
        };
        
        this.history.push(state);

        // Enforce history size limit
        if (this.history.length > this.maxHistorySize) {
            this.history.shift();
        }
        
        // Update history index to point to the latest state
        this.historyIndex = this.history.length - 1;
    }
    
    undo() {
        if (this.canUndo()) {
            this.historyIndex--;
            this.restoreState(this.history[this.historyIndex]);
            return true;
        }
        return false;
    }
    
    redo() {
        if (this.canRedo()) {
            this.historyIndex++;
            this.restoreState(this.history[this.historyIndex]);
            return true;
        }
        return false;
    }
    
    restoreState(state) {
        // Deep copy to avoid reference issues
        this.nodes = JSON.parse(JSON.stringify(state.nodes));
        this.connections = JSON.parse(JSON.stringify(state.connections));

        const storedVersion = typeof state.fontSizeVersion === 'number' ? state.fontSizeVersion : 1;
        if (storedVersion < FONT_SIZE_VERSION) {
            this.nodes.forEach(node => upgradeNodeFontMetrics(node));
            this.globalFontSize = scaleNumericValue(
                typeof state.globalFontSize === 'number' ? state.globalFontSize : BASE_GLOBAL_FONT_SIZE
            );
            this.fontSizeVersion = FONT_SIZE_VERSION;
        } else {
            this.globalFontSize =
                typeof state.globalFontSize === 'number' ? state.globalFontSize : DEFAULT_GLOBAL_FONT_SIZE;
            this.fontSizeVersion = storedVersion;
        }

        this.activeThreadId = state.activeThreadId ? state.activeThreadId.toString() : null;

        this.rebuildSpatialGrid();
        this.clearSelection();
    }
    
    canUndo() {
        return this.historyIndex > 0;
    }
    
    canRedo() {
        return this.historyIndex < this.history.length - 1;
    }
    
    // Data persistence
    saveToLocalStorage() {
        // Clean nodes for serialization - remove DOM elements but keep media URLs
        const cleanNodes = this.nodes.map(node => {
            const cleanNode = { ...node };
            // Remove DOM elements that can't be serialized
            delete cleanNode._imgObj;
            delete cleanNode._videoObj; 
            delete cleanNode._iframeObj;
            return cleanNode;
        });

        const data = {
            nodes: cleanNodes,
            connections: this.connections,
            offsetX: this.offsetX,
            offsetY: this.offsetY,
            scale: this.scale,
            globalFontSize: this.globalFontSize,
            activeThreadId: this.activeThreadId,
            fontSizeVersion: this.fontSizeVersion
        };

        try {
            localStorage.setItem('infiniteCanvasMVP', JSON.stringify(data));
            console.log('Canvas state saved successfully');
        } catch (e) {
            console.error('Error saving to localStorage:', e);
        }
    }
    
    loadFromLocalStorage() {
        const saved = localStorage.getItem('infiniteCanvasMVP');
        if (saved) {
            try {
                const data = JSON.parse(saved);
                let storedNodes = data.nodes || [];
                this.connections = data.connections || [];
                this.offsetX = data.offsetX || 0;
                this.offsetY = data.offsetY || 0;
                this.scale = data.scale || 1.0;
                const storedVersion = typeof data.fontSizeVersion === 'number' ? data.fontSizeVersion : 1;

                if (storedVersion < FONT_SIZE_VERSION) {
                    this.globalFontSize = scaleNumericValue(
                        typeof data.globalFontSize === 'number' ? data.globalFontSize : BASE_GLOBAL_FONT_SIZE
                    );
                    storedNodes = storedNodes.map(node => upgradeNodeFontMetrics(node));
                    this.fontSizeVersion = FONT_SIZE_VERSION;
                } else {
                    this.globalFontSize =
                        typeof data.globalFontSize === 'number' ? data.globalFontSize : DEFAULT_GLOBAL_FONT_SIZE;
                    this.fontSizeVersion = storedVersion;
                }

                this.nodes = storedNodes;
                this.activeThreadId = data.activeThreadId ? data.activeThreadId.toString() : null;

                // Ensure all nodes have fontSize and restore media reference properties
                this.nodes.forEach(node => {
                    if (typeof node.fontSize !== 'number') {
                        node.fontSize = this.globalFontSize;
                    }

                    if (node.threadId !== undefined && node.threadId !== null) {
                        node.threadId = node.threadId.toString();
                    }

                    // Initialize per-node generation states if they don't exist
                    // This ensures compatibility with nodes saved before per-node states were added
                    if (typeof node.isGeneratingAI !== 'boolean') {
                        node.isGeneratingAI = false;
                    }
                    if (typeof node.isGeneratingImage !== 'boolean') {
                        node.isGeneratingImage = false;
                    }
                    if (typeof node.isGeneratingVideo !== 'boolean') {
                        node.isGeneratingVideo = false;
                    }
                    if (typeof node.isGeneratingContext !== 'boolean') {
                        node.isGeneratingContext = false;
                    }
                    if (typeof node.isGeneratingReference !== 'boolean') {
                        node.isGeneratingReference = false;
                    }
                    if (typeof node.isUploadingImage !== 'boolean') {
                        node.isUploadingImage = false;
                    }
                    
                    // Restore media reference node properties
                    if (node.isMediaReference) {
                        // Ensure media reference properties are properly set
                        if (node.generatedImageUrl || node.generatedVideoUrl) {
                            // Set YouTube video flag if it's a YouTube URL
                            if (node.generatedVideoUrl && this.isYouTubeUrl(node.generatedVideoUrl)) {
                                node.isYouTubeVideo = true;
                            }
                            
                            // Ensure header height is set
                            if (!node._headerHeight) {
                                node._headerHeight = 50;
                            }
                        }
                    }
                });

                this.rebuildSpatialGrid();
                console.log('Canvas state loaded successfully');
                return true;
            } catch (e) {
                console.error('Error loading from localStorage:', e);
                this.createDefaultTemplate();
                return false;
            }
        } else {
            this.createDefaultTemplate();
            return false;
        }
    }
    
    // Helper method to check if URL is YouTube
    isYouTubeUrl(url) {
        try {
            const urlObj = new URL(url);
            return urlObj.hostname.includes('youtube.com') || urlObj.hostname.includes('youtu.be');
        } catch {
            return false;
        }
    }
    
    // Create a media reference node (images/videos)
    createMediaNode(url, mediaType, x = 400, y = 300) {
        const defaultWidth = 320;
        const defaultHeight = mediaType === 'image' ? 240 : 270; // Increased video height to account for header

        const node = this.createNode('', x, y, null, { save: false });
        node.width = defaultWidth;
        node.height = defaultHeight;
        node.isMediaReference = true;
        node.manuallyResized = true; // prevent auto resize
        node._headerHeight = 50; // Set header height

        if (mediaType === 'image') {
            node.generatedImageUrl = url;
        } else if (mediaType === 'youtube') {
            node.generatedVideoUrl = url;
            node.isYouTubeVideo = true;
            // Set custom dimensions for YouTube videos (accounting for header)
            node.width = 480;
            node.height = 320; // 270 (16:9 video) + 50 (header) = 320 total
        } else {
            node.generatedVideoUrl = url;
        }

        // Update spatial grid with new size
        this.removeNodeFromGrid(node);
        this.addNodeToGrid(node);

        this.saveState();
        return node;
    }
    
    // Helper to check if a URL is a blob URL (local file)
    isBlobUrl(url) {
        return url && url.startsWith('blob:');
    }
    
    createDefaultTemplate() {
        const centerX = 400;
        const centerY = 300;

        const mainNode = {
            id: Date.now().toString(),
            type: "text",
            x: centerX,
            y: centerY,
            width: 360,
            height: scaleNumericValue(140),
            text: "Tell me a joke 😄",
            fontSize: scaleNumericValue(16),
            isSelected: false,
            manuallyResized: false,
            maxHeight: scaleNumericValue(400),
            contentHeight: scaleNumericValue(140),
            scrollY: 0,
            // Per-node generation states
            isGeneratingAI: false,
            isGeneratingImage: false,
            isGeneratingVideo: false,
            isGeneratingContext: false,
            isGeneratingReference: false,
            isUploadingImage: false
        };

        const jokes = [
            {
                text: "Why don't scientists trust atoms?\n\n**Because they make up everything!** 🔬",
                x: centerX - 380,
                y: centerY + 150,
                model: "llama-3.3-70b-versatile"
            },
            {
                text: "What do you call a fake noodle?\n\n**An Impasta!** 🍝",
                x: centerX + 380,
                y: centerY + 150,
                model: "gpt-oss-120b"
            },
            {
                text: "Why did the scarecrow win an award?\n\n**Because he was outstanding in his field!** 🌾",
                x: centerX,
                y: centerY + 300,
                model: "qwen3-32b"
            }
        ];

        const jokeNodes = jokes.map((joke, index) => ({
            id: (Date.now() + index + 1).toString(),
            type: "text",
            x: joke.x,
            y: joke.y,
            width: 360,
            height: scaleNumericValue(140),
            text: joke.text,
            fontSize: scaleNumericValue(14),
            isSelected: false,
            manuallyResized: false,
            maxHeight: scaleNumericValue(400),
            contentHeight: scaleNumericValue(140),
            scrollY: 0,
            generatedByModel: joke.model,
            // Per-node generation states
            isGeneratingAI: false,
            isGeneratingImage: false,
            isGeneratingVideo: false,
            isGeneratingContext: false,
            isGeneratingReference: false,
            isUploadingImage: false
        }));

        const connections = jokeNodes.map(jokeNode => ({
            id: `conn_${mainNode.id}_${jokeNode.id}`,
            fromNode: mainNode.id.toString(),
            toNode: jokeNode.id.toString()
        }));

        this.nodes = [mainNode, ...jokeNodes];
        this.connections = connections;
        this.rebuildSpatialGrid();
    }
    
    // Spatial grid for performance
    addNodeToGrid(node) {
        const startX = Math.floor(node.x / this.gridSize);
        const endX = Math.floor((node.x + node.width) / this.gridSize);
        const startY = Math.floor(node.y / this.gridSize);
        const endY = Math.floor((node.y + node.height) / this.gridSize);

        for (let x = startX; x <= endX; x++) {
            for (let y = startY; y <= endY; y++) {
                const key = `${x},${y}`;
                if (!this.spatialGrid.has(key)) {
                    this.spatialGrid.set(key, new Set());
                }
                this.spatialGrid.get(key).add(node);
            }
        }
    }
    
    removeNodeFromGrid(node) {
        for (const [key, nodes] of this.spatialGrid.entries()) {
            nodes.delete(node);
            if (nodes.size === 0) {
                this.spatialGrid.delete(key);
            }
        }
    }
    
    rebuildSpatialGrid() {
        this.spatialGrid.clear();
        this.nodes.forEach(node => this.addNodeToGrid(node));
    }
    
    getNodeAtPoint(x, y) {
        const key = `${Math.floor(x / this.gridSize)},${Math.floor(y / this.gridSize)}`;
        const candidates = this.spatialGrid.get(key);
        if (!candidates) return null;

        const candidateArray = Array.from(candidates);
        for (let i = candidateArray.length - 1; i >= 0; i--) {
            const node = candidateArray[i];
            if (!this.isNodeInActiveThread(node)) continue;
            if (x >= node.x && x <= node.x + node.width &&
                y >= node.y && y <= node.y + node.height) {
                return node;
            }
        }
        return null;
    }

    resolveThreadId(threadId) {
        if (typeof threadId === 'undefined') {
            return this.activeThreadId ? this.activeThreadId.toString() : null;
        }
        if (threadId === null) {
            return null;
        }
        return threadId.toString();
    }

    setActiveThread(threadId) {
        const normalized = threadId ? threadId.toString() : null;
        if (this.activeThreadId !== normalized) {
            this.activeThreadId = normalized;
            this.clearSelection();
            this.selectedConnection = null;
        }
    }

    isNodeInActiveThread(node) {
        if (!node) {
            return false;
        }
        if (!this.activeThreadId) {
            return true;
        }
        if (node.threadId && node.threadId.toString() === this.activeThreadId) {
            return true;
        }
        // Allow legacy or non-chat nodes without explicit thread assignment
        if (!node.threadId && !node.isChatMessage) {
            return true;
        }
        return false;
    }
}
